import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import JobStatusBadge from '../components/JobStatusBadge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Eye, Search, Pencil, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Check, ChevronsUpDown, Upload, FileText, X, ClipboardList, CalendarRange } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { cn } from '../lib/utils';
import useJobConfig from '../hooks/useJobConfig';
import useEquipmentCatalog from '../hooks/useEquipmentCatalog';
import EquipmentFields from '../components/job/EquipmentFields';
import DateField from '../components/DateField';
import SiteFields, { emptySite } from '../components/customer/SiteFields';
import { RecordSiteFields } from '../components/customer/RecordSiteFields';
import { recordSite, recordSitePatch } from '../utils/siteLocation';
import './Jobs.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Jobs can carry a multi-equipment list; fall back to the legacy single fields.
const jobMakeModel = (job) => {
  const list = job.equipment || [];
  const first = list[0];
  const text = first
    ? [first.make, first.model].filter(Boolean).join(' ')
    : [job.make, job.model].filter(Boolean).join(' ');
  if (!text) return 'N/A';
  return list.length > 1 ? `${text} +${list.length - 1}` : text;
};

const jobSerial = (job) => (job.equipment || [])[0]?.serial_number || job.serial_number || 'N/A';

const initialFormData = {
  job_number: '',
  job_type: 'repair',
  customer_id: '',
  customer_name: '',
  customer_location: '',
  customer_lsd: '',
  site_details: null,
  description: '',
  make: '',
  model: '',
  serial_number: '',
  unit_id: '',
  equipment: [],
  status: 'intake',
  start_date: '',
  end_date: '',
  assigned_techs: []
};

export default function Jobs() {
  const { visibleStatuses, visibleTypes, labelForStatus, labelForType } = useJobConfig();
  const statusOptions = visibleStatuses.map((s) => ({ value: s.value, label: s.label }));
  const jobTypeOptions = visibleTypes.map((t) => ({ value: t.value, label: t.label }));
  const { types: equipmentTypes, refresh: refreshCatalog } = useEquipmentCatalog();

  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState(initialFormData);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, job: null });
  const [sortBy, setSortBy] = useState('job_number');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || 'all_active';
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [customerFilter, setCustomerFilter] = useState('all');
  const [customerComboOpen, setCustomerComboOpen] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', site: emptySite() });
  const [showArchived, setShowArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalJobs, setTotalJobs] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [archivedExcluded, setArchivedExcluded] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pdfTemplates, setPdfTemplates] = useState([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [scheduleTemplates, setScheduleTemplates] = useState([]);
  const [selectedScheduleTemplateId, setSelectedScheduleTemplateId] = useState('none');
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const pdfInputRef = useRef(null);

  // Sort customers alphabetically for all dropdowns
  const sortedCustomers = [...customers].sort((a, b) => a.name.localeCompare(b.name));

  const fetchJobs = useCallback(async (targetPage = 1, append = false, search = '') => {
    try {
      if (append) setLoadingMore(true);
      const response = await axios.get(`${API}/jobs`, {
        params: { page: targetPage, limit: pageSize, include_archived: showArchived, search },
      });
      const { items, total, has_more, archived_excluded } = response.data;
      setJobs((prev) => (append ? [...prev, ...items] : items));
      setTotalJobs(total);
      setHasMore(has_more);
      setArchivedExcluded(archived_excluded || 0);
      setPage(targetPage);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      if (append) setLoadingMore(false);
    }
  }, [pageSize, showArchived]);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (e) {
      console.error('Failed to load customers', e);
    }
  }, []);

  const fetchTechnicians = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/technicians`);
      setTechnicians(response.data);
    } catch (e) {
      console.error('Failed to load technicians', e);
    }
  }, []);

  useEffect(() => {
    fetchJobs(1, false);
    fetchCustomers();
    fetchTechnicians();
    axios.get(`${API}/pdf-templates`, { params: { division: 'shop' } }).then((res) => setPdfTemplates(res.data || [])).catch(() => {});
    if (user?.role === 'management') {
      axios.get(`${API}/schedule-templates`).then((res) => setScheduleTemplates(res.data || [])).catch(() => {});
    }
  }, [fetchJobs, fetchCustomers, fetchTechnicians]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Server-side search: the term is matched against job #, customer, description,
  // legacy make/model/serial/unit AND every equipment entry across the whole
  // database (not just the loaded page).
  useEffect(() => {
    const t = setTimeout(() => fetchJobs(1, false, searchTerm.trim()), 350);
    return () => clearTimeout(t);
  }, [searchTerm, fetchJobs]);

  const loadMore = () => {
    if (!hasMore || loadingMore) return;
    fetchJobs(page + 1, true, searchTerm.trim());
  };

  const openAddDialog = () => {
    setEditingJob(null);
    setCreatingCustomer(false);
    setNewCustomer({ name: '', site: emptySite() });
    setFormData(initialFormData);
    setPendingFiles([]);
    setSelectedTemplateIds([]);
    setSelectedScheduleTemplateId('none');
    setShowDialog(true);
  };

  const openEditDialog = (job) => {
    setEditingJob(job);
    const startDate = job.start_date ? new Date(job.start_date).toISOString().slice(0, 16) : '';
    const endDate = job.end_date ? new Date(job.end_date).toISOString().slice(0, 16) : '';
    setFormData({
      job_number: job.job_number,
      job_type: job.job_type || 'repair',
      customer_id: job.customer_id || '',
      customer_name: job.customer_name,
      customer_location: job.customer_location || '',
      customer_lsd: job.customer_lsd || '',
      site_details: job.site_details ?? null,
      description: job.description,
      make: job.make || '',
      model: job.model || '',
      serial_number: job.serial_number || '',
      unit_id: job.unit_id || '',
      equipment: job.equipment || [],
      status: job.status,
      start_date: startDate,
      end_date: endDate,
      assigned_techs: job.assigned_techs || []
    });
    setShowDialog(true);
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setFormData({
      ...formData,
      customer_id: customerId,
      customer_name: customer ? customer.name : '',
      customer_location: customer?.location || '',
      customer_lsd: '',
      site_details: null
    });
    setCreatingCustomer(false);
  };

  const handleCreateInlineCustomer = async () => {
    if (!newCustomer.name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    try {
      const payload = {
        name: newCustomer.name.trim(),
        sites: [newCustomer.site]
      };
      const res = await axios.post(`${API}/customers`, payload);
      toast.success('Customer created');
      
      // Set formData directly from the response (don't rely on stale customers state)
      const created = res.data;
      setFormData(prev => ({
        ...prev,
        customer_id: created.id,
        customer_name: created.name,
        ...recordSitePatch(created.sites?.[0] || emptySite(), true)
      }));
      setCreatingCustomer(false);
      setNewCustomer({ name: '', site: emptySite() });
      
      // Refresh customers list in the background
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    }
  };

  // Get the selected customer's data for location/LSD dropdowns
  const selectedCustomer = customers.find(c => c.id === formData.customer_id);

  const addPendingPdfs = (fileList) => {
    const files = Array.from(fileList);
    const pdfs = files.filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (pdfs.length < files.length) toast.error('Only PDF files can be attached here');
    if (pdfs.length) setPendingFiles((prev) => [...prev, ...pdfs]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.start_date || !formData.end_date) {
      toast.error('Start date and end date are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editingJob) {
        await axios.patch(`${API}/jobs/${editingJob.id}`, formData);
        toast.success('Job updated successfully');
      } else {
        const res = await axios.post(`${API}/jobs`, formData);
        const jobId = res.data.id;
        toast.success('Job created successfully');

        if (pendingFiles.length) {
          const uploadedUrls = [];
          let failed = 0;
          for (const file of pendingFiles) {
            try {
              const fd = new FormData();
              fd.append('file', file);
              const up = await axios.post(`${API}/jobs/${jobId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
              uploadedUrls.push(up.data.url);
            } catch { failed += 1; }
          }
          if (uploadedUrls.length) {
            try {
              await axios.post(`${API}/notes`, { job_id: jobId, content: 'Documents attached at job creation', attachments: uploadedUrls });
              toast.success(`${uploadedUrls.length} document(s) attached`);
            } catch { toast.error('Documents uploaded but could not be attached as a note'); }
          }
          if (failed) toast.error(`${failed} document(s) failed to upload — you can retry from the job page`);
        }

        for (const templateId of selectedTemplateIds) {
          try {
            await axios.post(`${API}/jobs/${jobId}/attach-template`, { template_id: templateId });
          } catch {
            const t = pdfTemplates.find((x) => x.id === templateId);
            toast.error(`Failed to attach form${t ? ` "${t.name}"` : ''}`);
          }
        }
        if (selectedTemplateIds.length) toast.success('Form(s) attached to job');

        if (selectedScheduleTemplateId && selectedScheduleTemplateId !== 'none') {
          try {
            const applied = await axios.post(`${API}/jobs/${jobId}/apply-template`, { template_id: selectedScheduleTemplateId });
            toast.success(`Schedule template applied — ${applied.data.segments.length} phases`);
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to apply schedule template');
          }
        }
      }
      await refreshCatalog();
      setShowDialog(false);
      setEditingJob(null);
      setFormData(initialFormData);
      setPendingFiles([]);
      setSelectedTemplateIds([]);
      setSelectedScheduleTemplateId('none');
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteJob = async () => {
    if (!deleteConfirm.job) return;
    try {
      await axios.delete(`${API}/jobs/${deleteConfirm.job.id}`);
      toast.success('Job deleted successfully');
      setDeleteConfirm({ open: false, job: null });
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete job');
    }
  };

  // Helper to extract numeric part from job number for sorting
  const extractNumber = (jobNumber) => {
    const match = jobNumber.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };

  const filteredAndSortedJobs = jobs
    .filter(job => {
      // Search is applied server-side (covers every equipment entry too).
      const matchesSearch = true;
      
      // Status filter - "all_active" excludes completed jobs
      let matchesStatus;
      if (statusFilter === 'all') {
        matchesStatus = true;
      } else if (statusFilter === 'all_active') {
        matchesStatus = job.status !== 'completed';
      } else {
        matchesStatus = job.status === statusFilter;
      }
      
      // Customer filter
      const matchesCustomer = customerFilter === 'all' || job.customer_id === customerFilter;
      
      return matchesSearch && matchesStatus && matchesCustomer;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'job_number':
          comparison = extractNumber(a.job_number) - extractNumber(b.job_number);
          break;
        case 'customer':
          comparison = (a.customer_name || '').localeCompare(b.customer_name || '');
          break;
        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '');
          break;
        case 'date':
          comparison = new Date(a.start_date || 0) - new Date(b.start_date || 0);
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  const canModifyJob = user?.role === 'management' || user?.role === 'foreman';
  const canDeleteJob = user?.role === 'management';

  return (
    <DashboardLayout>
      <div data-testid="jobs-page" className="space-y-6">
        <div className="flex flex-wrap gap-3 justify-between items-center">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Jobs</h3>
            <p className="text-slate-600">Manage all repair jobs</p>
          </div>
          {canModifyJob && (
            <Button onClick={openAddDialog} data-testid="create-job-button" className="bg-slate-900 hover:bg-slate-800">
              <Plus size={20} className="mr-2" />
              Create Job
            </Button>
          )}
        </div>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="grid-cols-[minmax(0,1fr)] max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="job-form-dialog">
            <DialogHeader>
              <DialogTitle>{editingJob ? 'Edit Job' : 'Create New Job'}</DialogTitle>
              <DialogDescription className="sr-only">Job, customer, location, equipment and schedule details.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="job-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="job_number">Job Number</Label>
                  <Input
                    id="job_number"
                    value={formData.job_number}
                    onChange={(e) => setFormData({ ...formData, job_number: e.target.value })}
                    required
                    data-testid="job-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="job_type">Job Type</Label>
                  <Select value={formData.job_type} onValueChange={(value) => setFormData({ ...formData, job_type: value })}>
                    <SelectTrigger data-testid="job-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {jobTypeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer">Customer</Label>
                {!creatingCustomer ? (
                  <>
                    <Popover open={customerComboOpen} onOpenChange={setCustomerComboOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={customerComboOpen}
                          className="w-full justify-between font-normal"
                          data-testid="customer-select"
                        >
                          {formData.customer_id
                            ? sortedCustomers.find(c => c.id === formData.customer_id)?.name
                            : "Select customer..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] max-w-[90vw] p-0 z-[100]" align="start">
                        <Command>
                          <CommandInput placeholder="Search customers..." data-testid="customer-search-input" />
                          <CommandList>
                            <CommandEmpty>No customer found.</CommandEmpty>
                            <CommandGroup>
                              {sortedCustomers.map(customer => (
                                <CommandItem
                                  key={customer.id}
                                  value={customer.name}
                                  onSelect={() => {
                                    handleCustomerChange(customer.id);
                                    setCustomerComboOpen(false);
                                  }}
                                  data-testid={`customer-option-${customer.id}`}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      formData.customer_id === customer.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {customer.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <button
                      type="button"
                      onClick={() => setCreatingCustomer(true)}
                      className="text-xs text-blue-600 hover:text-blue-800 mt-1"
                      data-testid="new-customer-link"
                    >
                      + New Customer
                    </button>
                  </>
                ) : (
                  <div className="space-y-3 p-3 border border-blue-200 bg-blue-50/50 rounded-lg" data-testid="new-customer-form">
                    <Input
                      placeholder="Customer name *"
                      value={newCustomer.name}
                      onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                      data-testid="new-customer-name"
                    />
                    <SiteFields value={newCustomer.site} onChange={(site) => setNewCustomer({ ...newCustomer, site })} testPrefix="new-customer-site" />
                    <div className="flex gap-2">
                      <Button type="button" size="sm" onClick={handleCreateInlineCustomer} className="bg-slate-900" data-testid="save-new-customer">
                        Create & Select
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => setCreatingCustomer(false)} data-testid="cancel-new-customer">
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {!creatingCustomer && <RecordSiteFields
                value={recordSite(formData, selectedCustomer?.sites, true)}
                onChange={(site) => setFormData((prev) => ({ ...prev, ...recordSitePatch(site, true) }))}
                customer={selectedCustomer} onCustomerUpdated={fetchCustomers} testPrefix="job-site"
              />}

              <div className="rounded-md border border-slate-200 p-3">
                <EquipmentFields
                  value={formData.equipment}
                  onChange={(equipment) => setFormData((prev) => ({ ...prev, equipment }))}
                  types={equipmentTypes}
                  customerId={formData.customer_id || null}
                  customer={selectedCustomer}
                  site={recordSite(formData, selectedCustomer?.sites, true)}
                  onReuseSite={(site, row) => setFormData((prev) => ({ ...prev,
                    ...(!prev.customer_id && row.customer_id ? { customer_id: row.customer_id, customer_name: row.customer_name } : {}),
                    ...recordSitePatch(site, true) }))}
                  testPrefix="job-equipment"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="description-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date <span className="text-red-500">*</span></Label>
                  <DateField
                    withTime
                    value={formData.start_date}
                    onChange={(v) => setFormData({ ...formData, start_date: v })}
                    data-testid="start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date <span className="text-red-500">*</span></Label>
                  <DateField
                    withTime
                    value={formData.end_date}
                    onChange={(v) => setFormData({ ...formData, end_date: v })}
                    data-testid="end-date-input"
                  />
                </div>
              </div>

              {!editingJob && (
                <div className="space-y-4 border-t border-slate-200 pt-4">
                  {user?.role === 'management' && scheduleTemplates.length > 0 && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1.5"><CalendarRange size={14} /> Schedule Template</Label>
                      <Select value={selectedScheduleTemplateId} onValueChange={setSelectedScheduleTemplateId}>
                        <SelectTrigger data-testid="create-job-schedule-template-select">
                          <SelectValue placeholder="None — no phase plan" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None — no phase plan</SelectItem>
                          {scheduleTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.name} ({t.tasks?.length || 0} phases)</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-400">Applies the template's phases to the new job, anchored at its start date.</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Documents (PDF)</Label>
                    <input
                      type="file"
                      ref={pdfInputRef}
                      accept=".pdf,application/pdf"
                      multiple
                      className="hidden"
                      onChange={(e) => { addPendingPdfs(e.target.files); e.target.value = ''; }}
                      data-testid="create-job-pdf-input"
                    />
                    <div
                      onClick={() => pdfInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={(e) => { e.preventDefault(); setDragOver(false); addPendingPdfs(e.dataTransfer.files); }}
                      className={`border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-colors ${dragOver ? 'border-slate-900 bg-slate-100' : 'border-slate-300 hover:border-slate-400'}`}
                      data-testid="create-job-dropzone"
                    >
                      <Upload size={18} className="mx-auto text-slate-400 mb-1" />
                      <p className="text-sm text-slate-500">Drop PDFs here, or click to browse</p>
                    </div>
                    {pendingFiles.length > 0 && (
                      <div className="space-y-1.5">
                        {pendingFiles.map((f, i) => (
                          <div key={`${f.name}-${i}`} className="flex items-center justify-between px-3 py-1.5 bg-slate-50 border border-slate-200 rounded text-sm" data-testid={`pending-file-${i}`}>
                            <span className="flex items-center gap-2 truncate"><FileText size={14} className="text-red-500 shrink-0" />{f.name}</span>
                            <button type="button" onClick={() => setPendingFiles((prev) => prev.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500 shrink-0 ml-2" data-testid={`remove-pending-file-${i}`}>
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {pdfTemplates.length > 0 && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1.5"><ClipboardList size={14} /> Attach Forms</Label>
                      <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="create-job-forms-list">
                        {pdfTemplates.map((t) => {
                          const checked = selectedTemplateIds.includes(t.id);
                          return (
                            <label key={t.id} className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer text-sm transition-colors ${checked ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-400'}`} data-testid={`create-job-form-${t.id}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => setSelectedTemplateIds((prev) => checked ? prev.filter((id) => id !== t.id) : [...prev, t.id])}
                                className="accent-slate-900"
                              />
                              <FileText size={14} className="text-slate-500" />
                              <span className="truncate">{t.name}</span>
                              <span className="text-xs text-slate-400 ml-auto shrink-0">{t.fields?.length || 0} fields</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Button type="submit" disabled={submitting} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-job-button">
                {submitting ? 'Saving...' : (editingJob ? 'Update Job' : 'Create Job')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Search and Filters */}
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search Bar */}
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <Input
                type="text"
                placeholder="Search all jobs by job #, customer, make, model, serial #, unit ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-jobs-input"
              />
            </div>

            {/* Status Filter */}
            <div className="w-44">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Jobs</SelectItem>
                  <SelectItem value="all_active">All Active</SelectItem>
                  {statusOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Customer Filter */}
            <div className="w-44">
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger data-testid="customer-filter">
                  <SelectValue placeholder="All Customers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  {sortedCustomers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sort By */}
            <div className="w-36">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger data-testid="sort-by">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="job_number">Sort by Job #</SelectItem>
                  <SelectItem value="customer">Sort by Customer</SelectItem>
                  <SelectItem value="status">Sort by Status</SelectItem>
                  <SelectItem value="date">Sort by Date</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sort Order */}
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              data-testid="sort-order-btn"
            >
              {sortOrder === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
            </Button>
          </div>

          {/* Archived toggle + result counts */}
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none text-slate-700" data-testid="show-archived-toggle">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="h-4 w-4"
                data-testid="show-archived-checkbox"
              />
              Show archived
              {!showArchived && archivedExcluded > 0 && (
                <span className="text-slate-500">({archivedExcluded} hidden — completed &gt;6 months ago)</span>
              )}
            </label>
            <div className="text-slate-500" data-testid="jobs-count-label">
              {searchTerm.trim().length >= 2 && hasMore
                ? <>Searching all jobs… <span className="text-slate-400">({jobs.length} of {totalJobs} loaded)</span></>
                : <>Showing {jobs.length} of {totalJobs}</>}
            </div>
          </div>
        </div>

        {/* Jobs Table */}
        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full jobs-table" data-testid="jobs-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Job #</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Make/Model</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Serial #</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredAndSortedJobs.map((job) => (
                  <tr key={job.id} className="hover:bg-slate-50" data-testid={`job-row-${job.id}`}>
                    <td data-label="Job #" className="px-6 py-4 text-sm font-mono text-slate-900">{job.job_number}</td>
                    <td data-label="Type" className="px-6 py-4 text-sm text-slate-600">{labelForType(job.job_type) || 'N/A'}</td>
                    <td data-label="Customer" className="px-6 py-4 text-sm text-slate-900">{job.customer_name}</td>
                    <td data-label="Make / Model" className="px-6 py-4 text-sm text-slate-600">{jobMakeModel(job)}</td>
                    <td data-label="Serial #" className="px-6 py-4 text-sm font-mono text-slate-600">{jobSerial(job)}</td>
                    <td data-label="Status" className="px-6 py-4">
                      <JobStatusBadge status={job.status} />
                    </td>
                    <td data-label="Actions" className="px-6 py-4">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/jobs/${job.id}`)}
                          data-testid={`view-job-${job.id}`}
                        >
                          <Eye size={16} className="mr-1" />
                          View
                        </Button>
                        {canModifyJob && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(job)}
                            data-testid={`edit-job-${job.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                        )}
                        {canDeleteJob && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteConfirm({ open: true, job })}
                            data-testid={`delete-job-${job.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredAndSortedJobs.length === 0 && (
              <div className="p-12 text-center text-slate-600">
                {searchTerm ? 'No jobs match your search' : 'No jobs found'}
              </div>
            )}
          </div>
          {hasMore && (
            <div className="border-t border-slate-200 p-4 flex justify-center">
              <Button
                variant="outline"
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="load-more-jobs-btn"
              >
                {loadingMore ? 'Loading…' : `Load more (${totalJobs - jobs.length} remaining)`}
              </Button>
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => setDeleteConfirm({ open, job: open ? deleteConfirm.job : null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Job</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete job <strong>{deleteConfirm.job?.job_number}</strong>? 
                This will permanently remove the job and all associated data including notes, assignments, and attachments. 
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteJob} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
                Delete Job
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
